import { Fragment, useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Upload, ChevronRight, ChevronLeft, CheckCircle2, FileText, Loader2, Check, AlertCircle, Route as RouteIcon } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { autoMap, buildLead, LEAD_FIELDS, type LeadTargetField } from "@/lib/leadFields";
import { getDefaultColumnMapping, saveDefaultColumnMapping } from "@/services/columnMappings";
import { insertLeadsBatch, type InsertableLead } from "@/services/leads";
import { createLeadImport, updateLeadImportCounts } from "@/services/leadImports";
import { runGeocodeLeads } from "@/services/geocode";
import { cn } from "@/lib/cn";

type Step = "upload" | "map" | "review" | "done";

interface ParsedFile {
  filename: string;
  headers: string[];
  rows: Record<string, string>[];
}

export function LeadsImport() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>("upload");
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [mapping, setMapping] = useState<Record<string, LeadTargetField>>({});
  const [defaultMapping, setDefaultMapping] = useState<Record<string, LeadTargetField> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [insertedCount, setInsertedCount] = useState(0);
  const [insertedIds, setInsertedIds] = useState<string[]>([]);
  // Live progress for the batched insert. Drives the percent bar shown in
  // the confirm step so users see continuous feedback during 10k+ imports
  // (Route4Me's failure mode was a static spinner that looked dead).
  const [importProgress, setImportProgress] = useState<{ inserted: number; total: number } | null>(null);
  const [autoGeocodeSummary, setAutoGeocodeSummary] = useState<{
    geocoded: number;
    cached: number;
    failed: number;
    error?: string;
  } | null>(null);

  useEffect(() => {
    if (!user?.teamId) return;
    let alive = true;
    getDefaultColumnMapping(user.teamId)
      .then((m) => {
        if (alive) setDefaultMapping(m);
      })
      .catch(() => {
        if (alive) setDefaultMapping(null);
      });
    return () => {
      alive = false;
    };
  }, [user?.teamId]);

  function mergeSuggestedMapping(
    headers: string[],
    suggested: Record<string, LeadTargetField>,
    saved: Record<string, LeadTargetField> | null,
  ) {
    if (!saved) return suggested;
    const merged = { ...suggested };
    for (const header of headers) {
      const savedTarget = saved[header];
      if (savedTarget) merged[header] = savedTarget;
    }
    return merged;
  }

  function handleFile(file: File) {
    // Guard drops of non-CSV files (click path is filtered by accept=).
    const isCsv = /\.csv$/i.test(file.name) || file.type === "text/csv" || file.type === "application/vnd.ms-excel";
    if (!isCsv) {
      toast.error("Only .csv files are supported.");
      return;
    }
    setParsing(true);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (h) => h.trim(),
      complete: (result) => {
        const rows = (result.data ?? []).filter((r) => Object.values(r).some((v) => v && String(v).trim()));
        const headers = result.meta.fields ?? [];
        if (!headers.length) {
          toast.error("No header row detected. The first row must contain column names.");
          setParsing(false);
          return;
        }
        const parsedFile: ParsedFile = { filename: file.name, headers, rows };
        const suggested = autoMap(headers);
        setParsed(parsedFile);
        setMapping(mergeSuggestedMapping(headers, suggested, defaultMapping));
        setStep("map");
        setParsing(false);
      },
      error: (err) => {
        toast.error(`CSV parse error: ${err.message}`);
        setParsing(false);
      },
    });
  }

  function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    handleFile(file);
  }

  const built = useMemo(() => {
    if (!parsed) return [] as InsertableLead[];
    return parsed.rows.map((row) => ({
      ...buildLead(row, mapping),
      team_id: user?.teamId ?? "",
      import_id: null as string | null,
      status: "new" as const,
    }));
  }, [parsed, mapping, user?.teamId]);

  async function autoGeocodeAfterImport(targetRows: number) {
    let geocoded = 0;
    let cached = 0;
    let failed = 0;
    let processed = 0;

    // Bound effort so import completion stays responsive.
    for (let i = 0; i < 5; i++) {
      const r = await runGeocodeLeads(500);
      geocoded += r.geocoded;
      cached += r.cached_hits;
      failed += r.failed;
      processed += r.processed;
      if (r.processed === 0) break;
      if (processed >= targetRows) break;
    }

    return { geocoded, cached, failed };
  }

  const stats = useMemo(() => {
    const total = built.length;
    const withName = built.filter((l) => l.first_name || l.last_name || l.full_name).length;
    const withAddress = built.filter((l) => l.address_street || l.address_combined).length;
    const withPhone = built.filter((l) => l.phone || l.phone_alt).length;
    const targets = new Set(Object.values(mapping).filter((t) => t !== "skip" && t !== "custom"));
    return { total, withName, withAddress, withPhone, mapped: targets.size };
  }, [built, mapping]);

  async function onConfirm() {
    if (!user || !parsed) return;
    if (submitting) return;
    setSubmitting(true);
    const t = toast.loading(`Importing ${built.length} leads…`);
    try {
      try {
        await saveDefaultColumnMapping(user.teamId, user.id, mapping);
        setDefaultMapping(mapping);
      } catch {
        // Non-blocking: import should continue even if preset save fails.
      }

      const importId = await createLeadImport({
        team_id: user.teamId,
        uploaded_by: user.id,
        filename: parsed.filename,
        row_count: parsed.rows.length,
        rows_inserted: 0,        // will be patched after insert below
        rows_skipped: 0,
        mapping,
        source_headers: parsed.headers,
      });
      const withImportId = built.map((l) => ({ ...l, import_id: importId }));
      // Progress callback fires after each 200-row batch commits so the
      // toast updates with "x / total imported…". Without this the user
      // stared at a static "Importing…" toast for the full duration of a
      // large import — Route4Me's mitigation was to cap uploads at 100;
      // ours is to give live feedback so users know it's working.
      const { inserted, ids } = await insertLeadsBatch(withImportId, 200, (p) => {
        toast.loading(`Importing ${p.inserted.toLocaleString()} / ${p.total.toLocaleString()} leads…`, { id: t });
        setImportProgress(p);
      });
      setInsertedCount(inserted);
      setInsertedIds(ids);
      setImportProgress(null);

      // Best-effort: patch the import row with the real inserted count and
      // fire the activity_log entry. Non-blocking — import already succeeded.
      try {
        await updateLeadImportCounts(
          importId,
          {
            rows_inserted: inserted,
            rows_skipped: parsed.rows.length - inserted,
          },
          { filename: parsed.filename },
        );
      } catch {
        // swallowed by design
      }

      // Resolve the import UX NOW — the leads are committed. Geocoding runs
      // after this and can take minutes on large imports; holding the loading
      // toast/step hostage to it made a successful import look hung at "N / N".
      toast.success(`Imported ${inserted} leads`, { id: t });
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      setStep("done");

      // Auto-geocode so imported leads become route-ready without a second
      // manual action. Fire-and-forget: awaiting it would hold `submitting`
      // true and block "Import another" until geocoding ends. The Done step
      // shows a live "geocoding…" hint until the summary lands.
      void autoGeocodeAfterImport(inserted)
        .then((g) => setAutoGeocodeSummary(g))
        .catch((geocodeErr: unknown) => {
          const geocodeMessage = geocodeErr instanceof Error ? geocodeErr.message : "Automatic geocoding failed";
          setAutoGeocodeSummary({ geocoded: 0, cached: 0, failed: 0, error: geocodeMessage });
        });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Import failed";
      toast.error(msg, { id: t });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <Stepper step={step} />

      {step === "upload" && (
        <UploadStep parsing={parsing} onFileChange={onFileChange} onFileDrop={handleFile} />
      )}

      {step === "map" && parsed && (
        <MapStep
          parsed={parsed}
          mapping={mapping}
          onMappingChange={setMapping}
          onBack={() => setStep("upload")}
          onNext={() => setStep("review")}
        />
      )}

      {step === "review" && parsed && (
        <ReviewStep
          stats={stats}
          submitting={submitting}
          previewLeads={built.slice(0, 3)}
          importProgress={importProgress}
          onBack={() => setStep("map")}
          onConfirm={onConfirm}
        />
      )}

      {step === "done" && (
        <DoneStep
          inserted={insertedCount}
          geocodeSummary={autoGeocodeSummary}
          onViewList={() => navigate("/leads")}
          onPlanRoute={() => {
            if (insertedIds.length === 0) {
              navigate("/routes/plan");
              return;
            }
            // URL length safety: ~36 chars per UUID + comma ≈ 37. Cap at ~200
            // IDs (~7.4kB) to stay well under browser URL limits. Remaining
            // leads still surface via the planner pool / filters.
            const carry = insertedIds.slice(0, 200);
            navigate(`/routes/plan?leadIds=${encodeURIComponent(carry.join(","))}`);
          }}
          onImportAnother={() => {
            setParsed(null);
            setMapping({});
            setInsertedCount(0);
            setInsertedIds([]);
            setAutoGeocodeSummary(null);
            setStep("upload");
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Sub-components
// ---------------------------------------------------------------------------

function Stepper({ step }: { step: Step }) {
  const steps: { key: Step; label: string }[] = [
    { key: "upload", label: "Upload CSV" },
    { key: "map", label: "Map columns" },
    { key: "review", label: "Review" },
    { key: "done", label: "Done" },
  ];
  const currentIdx = steps.findIndex((s) => s.key === step);
  return (
    <ol className="flex items-center gap-2 text-sm">
      {steps.map((s, i) => {
        const active = i === currentIdx;
        const done = i < currentIdx;
        return (
          <li key={s.key} className="flex items-center gap-2">
            <span
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold",
                done ? "bg-emerald-600 text-white"
                  : active ? "bg-brand-600 text-white"
                  : "bg-slate-200 text-slate-500",
              )}
            >
              {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <span className={cn("font-medium", active ? "text-slate-900" : "text-slate-500")}>{s.label}</span>
            {i < steps.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300" />}
          </li>
        );
      })}
    </ol>
  );
}

function UploadStep({ parsing, onFileChange, onFileDrop }: {
  parsing: boolean;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onFileDrop: (file: File) => void;
}) {
  // Counter, not boolean: dragenter/dragleave fire for every child element
  // crossed, so a plain flag flickers off mid-hover.
  const [dragDepth, setDragDepth] = useState(0);
  const dragging = dragDepth > 0;
  return (
    <label
      htmlFor="csv-upload"
      onDragEnter={(e) => { e.preventDefault(); setDragDepth((d) => d + 1); }}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
      onDragLeave={(e) => { e.preventDefault(); setDragDepth((d) => Math.max(0, d - 1)); }}
      onDrop={(e) => {
        e.preventDefault();
        setDragDepth(0);
        if (parsing) return;
        const file = e.dataTransfer.files?.[0];
        if (file) onFileDrop(file);
      }}
      className={cn(
        "flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed bg-white px-6 py-16 text-center transition",
        dragging
          ? "border-brand-500 bg-brand-50/60 ring-2 ring-brand-200"
          : "border-slate-300 hover:border-brand-400 hover:bg-brand-50/40",
      )}
    >
      {parsing ? (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-brand-500" />
          <p className="mt-3 text-sm font-medium text-slate-700">Parsing…</p>
        </>
      ) : (
        <>
          <Upload className={cn("h-10 w-10", dragging ? "text-brand-500" : "text-slate-400")} />
          <p className="mt-3 text-sm font-semibold text-slate-700">
            {dragging ? "Drop your CSV here" : "Click to upload or drag & drop a CSV"}
          </p>
          <p className="mt-1 text-xs text-slate-500">First row must be the header. Up to ~50,000 rows per file.</p>
        </>
      )}
      <input
        id="csv-upload"
        type="file"
        accept=".csv,text/csv"
        className="sr-only"
        onChange={onFileChange}
        disabled={parsing}
      />
    </label>
  );
}

type FieldGroup = {
  title: string;
  required?: "name" | "address";
  keys: LeadTargetField[];
};

const FIELD_GROUPS: FieldGroup[] = [
  { title: "Name", required: "name", keys: ["first_name", "last_name", "full_name"] },
  { title: "Address", required: "address", keys: ["address_street", "address_city", "address_state", "address_zip", "address_combined"] },
  { title: "Contact", keys: ["email", "phone", "phone_alt"] },
  { title: "More", keys: ["dob", "source", "notes"] },
];

function MapStep({
  parsed, mapping, onMappingChange, onBack, onNext,
}: {
  parsed: ParsedFile;
  mapping: Record<string, LeadTargetField>;
  onMappingChange: (next: Record<string, LeadTargetField>) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const [showSamples, setShowSamples] = useState(false);
  const [hideUnmapped, setHideUnmapped] = useState(false);

  // Reverse lookup: which CSV header (if any) is currently mapped to each internal field.
  const headerByField: Partial<Record<LeadTargetField, string>> = {};
  for (const [h, t] of Object.entries(mapping)) {
    if (t !== "skip" && t !== "custom" && !(t in headerByField)) {
      headerByField[t] = h;
    }
  }

  // Sample value from the first non-empty row, used as a hint next to each field.
  const sampleRow = parsed.rows.find((r) => Object.values(r).some((v) => v && String(v).trim()));

  function setFieldHeader(field: LeadTargetField, header: string | "") {
    const next: Record<string, LeadTargetField> = { ...mapping };
    // Demote any header currently claiming this field back to custom.
    for (const [h, t] of Object.entries(next)) {
      if (t === field) next[h] = "custom";
    }
    if (header) next[header] = field;
    onMappingChange(next);
  }

  function toggleHeaderSkip(header: string, skip: boolean) {
    onMappingChange({ ...mapping, [header]: skip ? "skip" : "custom" });
  }

  function resetAuto() {
    onMappingChange(autoMap(parsed.headers));
  }

  // Validation: a name and an address signal are both expected.
  const hasName = !!(headerByField.first_name || headerByField.last_name || headerByField.full_name);
  const hasAddress = !!(headerByField.address_street || headerByField.address_combined);

  // CSV columns that aren't claimed by any internal field — they flow into custom_fields.
  const extraHeaders = parsed.headers.filter((h) => {
    const t = mapping[h];
    return t === "custom" || t === "skip";
  });

  // Total fields mapped (excludes meta keys) — used in the top summary line.
  const mappedCount = Object.keys(headerByField).length;
  const totalMappable = FIELD_GROUPS.reduce((acc, g) => acc + g.keys.length, 0);

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      {/* File pill + reset */}
      <div className="flex flex-wrap items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600 ring-1 ring-slate-200">
        <FileText className="h-4 w-4 text-slate-400" />
        <span className="font-medium text-slate-700">{parsed.filename}</span>
        <span className="text-slate-400">·</span>
        <span>{parsed.rows.length.toLocaleString()} rows</span>
        <span className="text-slate-400">·</span>
        <span>{parsed.headers.length} columns</span>
        <button
          type="button"
          onClick={resetAuto}
          className="ml-auto rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
        >
          Reset auto-detect
        </button>
      </div>

      {/* Top status — single line, plain English */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1.5 text-slate-700">
            <Check className="h-4 w-4 text-emerald-600" />
            <span className="font-medium">{mappedCount}</span> of {totalMappable} fields mapped
          </span>
          {extraHeaders.length > 0 && (
            <span className="text-slate-500">
              · <span className="font-medium text-slate-700">{extraHeaders.length}</span> extra column{extraHeaders.length === 1 ? "" : "s"} → custom fields
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs">
          <label className="inline-flex items-center gap-1.5 text-slate-600">
            <input
              type="checkbox"
              checked={hideUnmapped}
              onChange={(e) => setHideUnmapped(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            Hide unmapped fields
          </label>
          <button
            type="button"
            onClick={() => setShowSamples((v) => !v)}
            className="font-medium text-brand-700 hover:underline"
          >
            {showSamples ? "Hide sample values" : "Preview sample values"}
          </button>
        </div>
      </div>

      {(!hasName || !hasAddress) && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <span>
            {!hasName && <>Map a <strong>name</strong> field (First + Last, or Full name)</>}
            {!hasName && !hasAddress && " and "}
            {!hasAddress && <>map an <strong>address</strong> (Street or Combined) so leads can be geocoded</>}.
          </span>
        </div>
      )}

      {/* Column legend — small, quiet, learned once */}
      <div className="grid grid-cols-[10rem_1fr] gap-3 px-4 text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:grid-cols-[12rem_1fr]">
        <span title="Routes Made Simple's internal lead fields">Routes Made Simple field</span>
        <span title="The column name from your uploaded CSV">Your CSV column</span>
      </div>

      {/* Grouped field cards */}
      {FIELD_GROUPS.map((group) => {
        const satisfied =
          group.required === "name" ? hasName
          : group.required === "address" ? hasAddress
          : null;
        const visibleKeys = hideUnmapped
          ? group.keys.filter((k) => !!headerByField[k])
          : group.keys;
        // If every row in this group is hidden by the filter, skip the card entirely.
        if (visibleKeys.length === 0) return null;
        return (
          <section key={group.title} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{group.title}</h3>
              {group.required && (
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium",
                    satisfied
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-amber-50 text-amber-800",
                  )}
                >
                  {satisfied ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                  {satisfied ? `${group.required} set` : `${group.required} required`}
                </span>
              )}
            </header>
            <div className="divide-y divide-slate-100">
              {visibleKeys.map((key) => {
                const def = LEAD_FIELDS.find((f) => f.key === key);
                if (!def) return null;
                const currentHeader = headerByField[key] ?? "";
                const sample = showSamples && currentHeader && sampleRow ? (sampleRow[currentHeader] ?? "").toString().slice(0, 80) : "";
                return (
                  <FieldRow
                    key={key}
                    label={def.label}
                    currentHeader={currentHeader}
                    sample={sample}
                    headers={parsed.headers}
                    onChange={(h) => setFieldHeader(key, h)}
                  />
                );
              })}
            </div>
          </section>
        );
      })}

      {extraHeaders.length > 0 && (
        <details className="rounded-md border border-slate-200 bg-white">
          <summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:bg-slate-50">
            Manage {extraHeaders.length} custom field{extraHeaders.length === 1 ? "" : "s"}
          </summary>
          <ul className="divide-y divide-slate-100">
            {extraHeaders.map((h) => {
              const skipped = mapping[h] === "skip";
              const sample = sampleRow ? (sampleRow[h] ?? "").toString().slice(0, 60) : "";
              return (
                <li key={h} className="flex items-center gap-3 px-3 py-1.5 text-xs">
                  <label className="flex min-w-0 flex-1 items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!skipped}
                      onChange={(e) => toggleHeaderSkip(h, !e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className={cn("truncate font-medium", skipped ? "text-slate-400 line-through" : "text-slate-700")}>
                      {h}
                    </span>
                  </label>
                  <span className="truncate text-slate-400">{sample}</span>
                </li>
              );
            })}
          </ul>
        </details>
      )}

      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!hasName || !hasAddress}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Continue
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function FieldRow({
  label,
  currentHeader,
  sample,
  headers,
  onChange,
}: {
  label: string;
  currentHeader: string;
  sample: string;
  headers: string[];
  onChange: (header: string) => void;
}) {
  const mapped = !!currentHeader;
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-3 px-4 py-3 sm:grid-cols-[12rem_1fr]">
      <div
        className="flex items-center gap-2"
        title="Routes Made Simple field — our internal lead column"
      >
        {mapped ? (
          <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
        ) : (
          <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
        )}
        <span className="text-sm font-medium text-slate-800">{label}</span>
      </div>
      <div className="min-w-0" title="Your CSV column — pick which header from your file feeds this field">
        <select
          value={currentHeader}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            "w-full rounded-md text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500",
            mapped ? "border-slate-300" : "border-slate-200 text-slate-500",
          )}
        >
          <option value="">— Not mapped —</option>
          {headers.map((h) => (
            <option key={h} value={h}>{h}</option>
          ))}
        </select>
        {sample && (
          <p className="mt-1 truncate text-xs text-slate-500">
            <span className="text-slate-400">sample:</span> <span className="font-medium text-slate-600">{sample}</span>
          </p>
        )}
      </div>
    </div>
  );
}

const PREVIEW_FIELDS: Array<{ key: keyof InsertableLead; label: string }> = [
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "full_name", label: "Full name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "phone_alt", label: "Alt. phone" },
  { key: "address_street", label: "Street" },
  { key: "address_city", label: "City" },
  { key: "address_state", label: "State" },
  { key: "address_zip", label: "ZIP" },
  { key: "address_combined", label: "Combined" },
  { key: "dob", label: "Date of birth" },
  { key: "source", label: "Lead source" },
  { key: "notes", label: "Notes" },
];

function ReviewStep({
  stats, submitting, previewLeads, importProgress, onBack, onConfirm,
}: {
  stats: { total: number; withName: number; withAddress: number; withPhone: number; mapped: number };
  submitting: boolean;
  previewLeads: InsertableLead[];
  importProgress: { inserted: number; total: number } | null;
  onBack: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Rows to import" value={stats.total.toLocaleString()} />
        <Stat label="Mapped fields" value={stats.mapped.toString()} />
        <Stat label="With a name" value={`${stats.withName.toLocaleString()} / ${stats.total.toLocaleString()}`} />
        <Stat label="With an address" value={`${stats.withAddress.toLocaleString()} / ${stats.total.toLocaleString()}`} />
      </div>

      {previewLeads.length > 0 && (
        <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <header className="border-b border-slate-200 bg-slate-50 px-4 py-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              First {previewLeads.length} lead{previewLeads.length === 1 ? "" : "s"} preview
            </h3>
          </header>
          <ul className="divide-y divide-slate-200">
            {previewLeads.map((lead, idx) => {
              const rows = PREVIEW_FIELDS.map((f) => ({ label: f.label, value: lead[f.key] as string | null }))
                .filter((r) => r.value && String(r.value).trim());
              const customCount = Object.keys(lead.custom_fields ?? {}).length;
              return (
                <li key={idx} className="px-4 py-3">
                  <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Row {idx + 1}
                  </div>
                  <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1.5 text-sm sm:grid-cols-[9rem_1fr]">
                    {rows.map((r) => (
                      <Fragment key={r.label}>
                        <dt className="text-slate-500">{r.label}</dt>
                        <dd className="truncate font-medium text-slate-800">{String(r.value)}</dd>
                      </Fragment>
                    ))}
                    {customCount > 0 && (
                      <>
                        <dt className="text-slate-500">+ custom</dt>
                        <dd className="text-xs text-slate-500">
                          {customCount} field{customCount === 1 ? "" : "s"} stored in <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">custom_fields</code>
                        </dd>
                      </>
                    )}
                  </dl>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <p className="text-sm text-slate-600">
        Anything mapped to <span className="font-medium">Custom field</span> lands in the lead's
        <code className="ml-1 rounded bg-slate-100 px-1 py-0.5 text-xs">custom_fields</code> JSON
        and stays searchable.
      </p>

      {/* Large-import heads-up. Estimate: 200 rows per ~200ms round-trip,
         so 10k rows ≈ 10s, 50k ≈ 50s. Surface the expected wait + reassure
         the user that closing the tab is OK (the insert continues server-
         side; geocoding runs after). */}
      {stats.total >= 5000 && !submitting && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <p className="font-semibold">Heads up — large import ({stats.total.toLocaleString()} leads)</p>
          <p className="mt-1">
            Expect about {Math.ceil(stats.total / 200 / 5)}–{Math.ceil(stats.total / 200 / 3)} seconds for the upload, then auto-geocoding kicks off in the background. You'll see live progress below; closing the tab won't cancel an upload that's already started.
          </p>
        </div>
      )}

      {/* Live progress bar — drives off the per-batch callback from
         insertLeadsBatch. Shown only while a batch is in flight. */}
      {submitting && importProgress && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center justify-between text-xs font-medium text-slate-700">
            <span>Importing leads…</span>
            <span>{importProgress.inserted.toLocaleString()} / {importProgress.total.toLocaleString()}</span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full bg-brand-600 transition-all duration-150"
              style={{ width: `${Math.min(100, Math.round((importProgress.inserted / Math.max(1, importProgress.total)) * 100))}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={submitting || stats.total === 0}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {submitting ? "Importing…" : `Import ${stats.total.toLocaleString()} leads`}
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function DoneStep({
  inserted, geocodeSummary, onViewList, onPlanRoute, onImportAnother,
}: {
  inserted: number;
  geocodeSummary: { geocoded: number; cached: number; failed: number; error?: string } | null;
  onViewList: () => void;
  onPlanRoute: () => void;
  onImportAnother: () => void;
}) {
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-8 text-center">
      <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
      <h3 className="mt-3 text-lg font-semibold text-emerald-900">
        Imported {inserted.toLocaleString()} leads
      </h3>
      {geocodeSummary?.error ? (
        <p className="mt-1 text-sm text-amber-800">
          Automatic geocoding failed: {geocodeSummary.error}
        </p>
      ) : geocodeSummary ? (
        <p className="mt-1 text-sm text-emerald-700">
          Auto-geocoded {geocodeSummary.geocoded} (cache hits {geocodeSummary.cached}, failed {geocodeSummary.failed}).
        </p>
      ) : (
        <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-emerald-700">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Auto-geocoding addresses in the background…
        </p>
      )}
      <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={onPlanRoute}
          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
        >
          <RouteIcon className="h-4 w-4" />
          Plan a route
        </button>
        <button
          type="button"
          onClick={onViewList}
          className="rounded-md border border-emerald-300 bg-white px-4 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
        >
          View leads
        </button>
        <button
          type="button"
          onClick={onImportAnother}
          className="rounded-md border border-slate-300 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Import another
        </button>
      </div>
    </div>
  );
}
